// ── managers.js — 매니저 초대·관리 (V49 → V95) ────────────────
//
// V95 변경: 사장 승인 단계 추가 (자동 매칭이 아닌 3단계 흐름)
//
// 데이터 구조:
//   /users/{ownerUid}/managers/{managerUid}
//     { email, addedAt, addedByUid, role: 'manager', permissions,
//       isActive: bool (V95) }
//
//   /invitations/{normalizedEmail}
//     { ownerUid, ownerEmail, invitedBy, createdAt, role: 'manager',
//       permissions, status: 'pending' | 'accepted' (V95) }
//
// 흐름 (V95):
//   1. 사장이 매니저 이메일 초대 → /invitations 작성 (status: 'pending')
//   2. 매니저가 같은 이메일로 가입 → acceptInvitationIfExists 호출
//      → managers/{uid} 생성 (isActive: false) + invitation status: 'accepted'
//   3. 사장이 SettingsPage에서 "승인" 버튼 클릭
//      → managers/{uid}.isActive = true + invitation 삭제
//   4. 매니저는 isActive: true가 된 매장만 매장 셀렉터에 노출됨

import {
  doc, getDoc, setDoc, deleteDoc, updateDoc,
  collection, getDocs, query, where,
  serverTimestamp, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import { db } from '../db';
import { normalizePermissions, DEFAULT_PERMISSIONS } from './permissions';

// 이메일을 Firestore doc id로 안전하게 변환 (단순 lowercase)
export function normalizeEmail(email) {
  if (!email) return '';
  return String(email).trim().toLowerCase();
}

// ── 1) 사장이 매니저 초대 ───────────────────────────────────
export async function inviteManager(ownerUid, ownerEmail, managerEmail, permissions = null) {
  const normEmail = normalizeEmail(managerEmail);
  if (!normEmail) throw new Error('이메일이 비어있습니다');
  if (!/.+@.+\..+/.test(normEmail)) throw new Error('이메일 형식이 올바르지 않습니다');
  if (normEmail === normalizeEmail(ownerEmail)) throw new Error('본인 이메일은 초대할 수 없습니다');

  const perms = normalizePermissions(permissions || DEFAULT_PERMISSIONS);

  await setDoc(doc(db, 'invitations', normEmail), {
    ownerUid,
    ownerEmail: ownerEmail || '',
    invitedBy: ownerUid,
    role: 'manager',
    permissions: perms,
    status: 'pending',  // V95
    createdAt: serverTimestamp(),
  });
}

// ── 2) 매니저가 가입 시 자동 호출 — invitation 매칭 + manager 등록 (isActive: false) ──
export async function acceptInvitationIfExists(userUid, userEmail) {
  if (!userEmail) return null;
  const normEmail = normalizeEmail(userEmail);
  const invRef = doc(db, 'invitations', normEmail);
  const invSnap = await getDoc(invRef);
  if (!invSnap.exists()) return null;

  const inv = invSnap.data();
  const ownerUid = inv.ownerUid;
  if (!ownerUid || ownerUid === userUid) {
    try { await deleteDoc(invRef); } catch (e) {}
    return null;
  }

  // V95: manager doc 생성 — isActive: false (사장 승인 대기)
  try {
    await setDoc(doc(db, 'users', ownerUid, 'managers', userUid), {
      email: normEmail,
      role: inv.role || 'manager',
      permissions: normalizePermissions(inv.permissions || DEFAULT_PERMISSIONS),
      isActive: false,                    // V95: 사장 승인 전
      addedAt: serverTimestamp(),
      addedByUid: inv.invitedBy || ownerUid,
    });
  } catch (e) {
    console.warn('manager doc create failed:', e?.message);
    return null;
  }

  // V95: invitation status를 'accepted'로 update (삭제는 사장 승인 후)
  try {
    await updateDoc(invRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      acceptedByUid: userUid,
    });
  } catch (e) { console.warn('invitation status update failed:', e?.message); }

  // 매니저 본인 user doc에 managedOwnerUids push
  try {
    await setDoc(doc(db, 'users', userUid), {
      managedOwnerUids: arrayUnion(ownerUid),
    }, { merge: true });
  } catch (e) { console.warn('managedOwnerUids push failed:', e?.message); }

  return ownerUid;
}

// ── 3) 사장이 매니저 승인 → isActive: true + invitation 삭제 ──
export async function approveManager(ownerUid, managerUid, managerEmail) {
  await updateDoc(doc(db, 'users', ownerUid, 'managers', managerUid), {
    isActive: true,
    approvedAt: serverTimestamp(),
  });
  // invitation 정리
  const normEmail = normalizeEmail(managerEmail);
  if (normEmail) {
    try { await deleteDoc(doc(db, 'invitations', normEmail)); } catch (e) {}
  }
}

// ── 3-alt) 사장이 가입한 매니저를 거부 — manager doc + invitation 둘 다 삭제 ──
export async function rejectManager(ownerUid, managerUid, managerEmail) {
  // 매니저 doc 삭제
  try { await deleteDoc(doc(db, 'users', ownerUid, 'managers', managerUid)); } catch (e) {}

  // 매니저 user doc의 managedOwnerUids에서 owner 제거
  try {
    await setDoc(doc(db, 'users', managerUid), {
      managedOwnerUids: arrayRemove(ownerUid),
    }, { merge: true });
  } catch (e) {}

  // invitation 삭제
  const normEmail = normalizeEmail(managerEmail);
  if (normEmail) {
    try { await deleteDoc(doc(db, 'invitations', normEmail)); } catch (e) {}
  }
}

// ── 4) 가입 안 한 초대 취소 (invitation만 삭제) ──
export async function cancelInvitation(managerEmail) {
  const normEmail = normalizeEmail(managerEmail);
  if (!normEmail) return;
  await deleteDoc(doc(db, 'invitations', normEmail));
}

// ── 5) 사장이 보낸 모든 invitation 목록 (가입 전·후 둘 다) ──
export async function listInvitations(ownerUid) {
  try {
    const q = query(collection(db, 'invitations'), where('invitedBy', '==', ownerUid));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ email: d.id, ...d.data() }));
  } catch (e) {
    console.warn('listInvitations failed:', e?.message);
    return [];
  }
}

// ── 6) V50: 기존 매니저 권한 수정 ──
export async function updateManagerPermissions(ownerUid, managerUid, permissions) {
  const perms = normalizePermissions(permissions);
  await updateDoc(doc(db, 'users', ownerUid, 'managers', managerUid), {
    permissions: perms,
    updatedAt: serverTimestamp(),
  });
}

// ── 7) 사장이 보유한 매니저 목록 (활성·비활성 둘 다 반환) ──
export async function listManagers(ownerUid) {
  const snap = await getDocs(collection(db, 'users', ownerUid, 'managers'));
  return snap.docs.map(d => ({ uid: d.id, ...d.data() }));
}

// ── 8) 사장이 매니저 해제 ──
export async function removeManager(ownerUid, managerUid) {
  await deleteDoc(doc(db, 'users', ownerUid, 'managers', managerUid));
}

// ── 9) 본인이 매니저로 들어가는 owner uid 목록 ──
export async function getManagedOwnerUids(userUid) {
  try {
    const userSnap = await getDoc(doc(db, 'users', userUid));
    if (!userSnap.exists()) return [];
    const data = userSnap.data();
    return Array.isArray(data.managedOwnerUids) ? data.managedOwnerUids : [];
  } catch (e) {
    return [];
  }
}
